/**
 * StorePublic — página pública partilhável da loja (/loja/:slug)
 * Acessível sem login. Carrinho livre, redireciona para login ao pagar.
 */
import React, { useEffect, useState } from 'react';
import { estaEsgotado } from '../utils/stock';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiShoppingBag, FiZap, FiSearch, FiShare2, FiLogIn,
  FiX, FiChevronRight, FiPackage, FiCheck, FiPlus,
  FiMinus, FiShoppingCart, FiStar, FiSmartphone, FiGlobe,
} from 'react-icons/fi';
import { IoStorefrontOutline } from 'react-icons/io5';
import api from '../services/api';
import { ecommerceService } from '../services';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import type { Product as CartProduct } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StoreInfo {
  ID: string;
  DESIGNACAO: string;
  IMAGEM?: string;
  MIME_TYPE?: string;
  DESCRICAO?: string;
  SLUG?: string;
}

interface Product {
  ID: string;
  NOME: string;
  DESCRICAO?: string;
  PRECO: number;
  PRECO_PROMOCIONAL?: number;
  IMAGEM?: string;
  MIME_TYPE?: string;
  STOCK?: number;
  TIPO?: string;
  CATEGORIA?: string;
}

interface Category {
  ID: string;
  NOME: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  new Intl.NumberFormat('pt-CV', { minimumFractionDigits: 0 }).format(v) + ' ECV';

const initials = (name: string) =>
  name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Sk: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-xl ${className}`} />
);

const ProductSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
    <Sk className="aspect-square rounded-none" />
    <div className="p-3 space-y-2">
      <Sk className="h-4 w-3/4" />
      <Sk className="h-3 w-1/2" />
      <Sk className="h-8 w-full mt-2 rounded-xl" />
    </div>
  </div>
);

// ─── Pagali Trust Bar ─────────────────────────────────────────────────────────

const TrustBar = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-white border-b border-gray-100">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-center gap-6 overflow-x-auto scrollbar-hide">
        {([
          { icon: FiCheck,       key: 'securePayment' },
          { icon: FiZap,         key: 'instantDelivery' },
          { icon: FiStar,        key: 'verifiedStore' },
          { icon: FiSmartphone,  key: 'localSupport' },
        ] as const).map(({ icon: Icon, key }) => (
          <div key={key} className="flex items-center gap-1.5 text-gray-500 flex-shrink-0">
            <div className="w-5 h-5 rounded-full bg-violet-50 flex items-center justify-center">
              <Icon className="text-violet-500 text-[10px]" />
            </div>
            <span className="text-[11px] font-medium whitespace-nowrap">{t(`store.${key}`)}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[11px] font-bold text-violet-600">Pagali</span>
        </div>
      </div>
    </div>
  );
};

// ─── Pagali Mid-page Ad ───────────────────────────────────────────────────────

const PagaliMidAd: React.FC<{ onCta: () => void }> = ({ onCta }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-4 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <span className="text-white font-black text-xs tracking-tight">P</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-gray-800">{t('store.hasShopCta')}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">{t('store.hasShopDesc')}</p>
        </div>
        <button
          onClick={onCta}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-xl bg-violet-600 text-white text-[11px] font-semibold hover:bg-violet-700 transition-colors"
        >
          {t('store.start')}
          <FiChevronRight className="text-[10px]" />
        </button>
      </div>
    </div>
  );
};

// ─── Pagali Services Section ──────────────────────────────────────────────────

const PagaliServices: React.FC<{ onRegister: () => void }> = ({ onRegister }) => {
  const services = [
    {
      icon: IoStorefrontOutline,
      color: 'bg-violet-50',
      iconColor: 'text-violet-600',
      title: 'Pagali Shop',
      desc: 'Loja online para vender produtos físicos e digitais.',
    },
    {
      icon: FiZap,
      color: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      title: 'Digital',
      desc: 'Entrega automática de ebooks, cursos e downloads.',
    },
    {
      icon: FiGlobe,
      color: 'bg-blue-50',
      iconColor: 'text-blue-600',
      title: 'Pagali Pay',
      desc: 'Aceita pagamentos com cartão, VINTI4 e carteira.',
    },
    {
      icon: FiSmartphone,
      color: 'bg-amber-50',
      iconColor: 'text-amber-600',
      title: 'Carteira',
      desc: 'Wallet digital para pagar e receber em Cabo Verde.',
    },
  ] as const;

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-violet-600 flex items-center justify-center">
            <span className="text-white font-black text-[10px]">P</span>
          </div>
          <span className="text-sm font-bold text-gray-800">Plataforma Pagali</span>
        </div>
        <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">Cabo Verde</span>
      </div>

      {/* Services grid */}
      <div className="grid grid-cols-2 gap-px bg-gray-100">
        {services.map(({ icon: Icon, color, iconColor, title, desc }) => (
          <div key={title} className="bg-white px-3 py-3">
            <div className={`w-8 h-8 rounded-xl ${color} flex items-center justify-center mb-2`}>
              <Icon className={`${iconColor} text-sm`} />
            </div>
            <p className="text-xs font-semibold text-gray-800">{title}</p>
            <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{desc}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
        <p className="text-xs text-gray-500">Cria a tua conta grátis hoje.</p>
        <button
          onClick={onRegister}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors"
        >
          Registar
          <FiChevronRight className="text-[10px]" />
        </button>
      </div>
    </div>
  );
};

// ─── Product Detail Sheet ─────────────────────────────────────────────────────

const ProductSheet: React.FC<{
  product: Product;
  onClose: () => void;
  onAdd: (p: Product) => void;
}> = ({ product, onClose, onAdd }) => {
  const { t } = useTranslation();
  const { cart } = useCart();
  const isDigital = product.TIPO === 'DIGITAL' || (product as any).tipo === 'DIGITAL';
  // `(STOCK ?? 1) === 0` nunca era true: a API manda `stock` minúsculo e como
  // string — era por aqui que produtos esgotados entravam no carrinho.
  const isOutOfStock = !isDigital && estaEsgotado(product);
  const price = product.PRECO || 0;
  const hasPromo = product.PRECO_PROMOCIONAL && product.PRECO_PROMOCIONAL < price;
  const currentPrice = hasPromo ? product.PRECO_PROMOCIONAL! : price;
  const discount = hasPromo ? Math.round((1 - product.PRECO_PROMOCIONAL! / price) * 100) : 0;
  const imageSrc = product.IMAGEM && product.MIME_TYPE
    ? `data:${product.MIME_TYPE};base64,${product.IMAGEM}` : null;
  const inCart = cart.find(i => i.ID === product.ID);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    if (isOutOfStock) return;
    onAdd({ ...product, STOCK: product.STOCK } as Product);
    // add qty times if >1
    for (let i = 1; i < qty; i++) onAdd(product);
    setAdded(true);
    setTimeout(() => { setAdded(false); onClose(); }, 900);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg mx-auto bg-white rounded-t-3xl shadow-2xl"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '85vh', overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 mb-0" />

        {/* Close */}
        <button onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/10 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:bg-black/20 transition-colors">
          <FiX className="text-sm" />
        </button>

        {/* Image — compact square to leave room for content */}
        <div className="relative aspect-square max-h-52 bg-gray-50 overflow-hidden">
          {imageSrc
            ? <img src={imageSrc} alt={product.NOME} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center bg-gray-100"><FiPackage className="text-5xl text-gray-200" /></div>
          }
          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {isDigital && (
              <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow">
                <FiZap className="text-[10px]" />DIGITAL
              </span>
            )}
            {hasPromo && (
              <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow">
                -{discount}%
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pt-4 pb-6 space-y-4">
          {/* Name + price */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 leading-snug">{product.NOME}</h2>
            <div className="flex items-baseline gap-2 mt-1.5">
              <span className={`text-2xl font-black ${hasPromo ? 'text-rose-600' : 'text-gray-900'}`}>
                {fmt(currentPrice)}
              </span>
              {hasPromo && (
                <span className="text-sm text-gray-400 line-through">{fmt(price)}</span>
              )}
            </div>
          </div>

          {/* Stock / delivery status */}
          <div className="flex items-center gap-2">
            {isDigital ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full">
                <FiZap />{t('store.immediateDelivery')}
              </span>
            ) : isOutOfStock ? (
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">{t('store.outOfStock')}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">
                <FiCheck />{t('store.inStock')}
                {(product.STOCK ?? 0) > 0 && (product.STOCK ?? 0) <= 5 && (
                  <span className="text-amber-600"> · {t('store.only')} {product.STOCK}</span>
                )}
              </span>
            )}
          </div>

          {/* Description */}
          {product.DESCRICAO && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t('store.description')}</h3>
              <div
                className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: product.DESCRICAO }}
              />
            </div>
          )}

          {/* Qty selector (physical only) */}
          {!isDigital && !isOutOfStock && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">{t('store.quantity')}</span>
              <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
                  <FiMinus className="text-xs" />
                </button>
                <span className="w-7 text-center text-sm font-bold text-gray-800">{qty}</span>
                <button onClick={() => setQty(q => Math.min(product.STOCK ?? 99, q + 1))}
                  className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
                  <FiPlus className="text-xs" />
                </button>
              </div>
              {inCart && <span className="text-xs text-violet-600">{inCart.QTD} no carrinho</span>}
            </div>
          )}

          {/* Add to cart CTA */}
          <button
            onClick={handleAdd}
            disabled={isOutOfStock || added}
            className={`w-full py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]
              ${added ? 'bg-emerald-500 text-white' : isOutOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-violet-600 text-white hover:bg-violet-700'}`}
            style={!isOutOfStock && !added ? { boxShadow: '0 4px 15px rgba(124,58,237,0.35)' } : {}}
          >
            {added
              ? <><FiCheck />{t('store.addedToCart')}</>
              : isOutOfStock
                ? t('store.outOfStock')
                : <><FiShoppingCart />{t('store.addToCart')}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Product Card ─────────────────────────────────────────────────────────────

const ProductCard: React.FC<{
  product: Product;
  onAdd: (p: Product) => void;
  onDetail: (p: Product) => void;
}> = ({ product, onAdd, onDetail }) => {
  const { t } = useTranslation();
  const { cart } = useCart();
  const isDigital = product.TIPO === 'DIGITAL' || (product as any).tipo === 'DIGITAL';
  // `(STOCK ?? 1) === 0` nunca era true: a API manda `stock` minúsculo e como
  // string — era por aqui que produtos esgotados entravam no carrinho.
  const isOutOfStock = !isDigital && estaEsgotado(product);
  const price = product.PRECO || 0;
  const hasPromo = product.PRECO_PROMOCIONAL && product.PRECO_PROMOCIONAL < price;
  const currentPrice = hasPromo ? product.PRECO_PROMOCIONAL! : price;
  const discount = hasPromo ? Math.round((1 - product.PRECO_PROMOCIONAL! / price) * 100) : 0;
  const imageSrc = product.IMAGEM && product.MIME_TYPE
    ? `data:${product.MIME_TYPE};base64,${product.IMAGEM}` : null;
  const inCart = cart.find(i => i.ID === product.ID);
  const [added, setAdded] = useState(false);

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOutOfStock) return;
    onAdd(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <div
      onClick={() => onDetail(product)}
      className={`bg-white rounded-2xl overflow-hidden flex flex-col group transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer ${isOutOfStock ? 'opacity-60' : ''}`}
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {imageSrc
          ? <img src={imageSrc} alt={product.NOME} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><FiPackage className="text-4xl text-gray-200" /></div>
        }
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {isDigital && (
            <span className="inline-flex items-center gap-0.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              <FiZap className="text-[9px]" />DIGITAL
            </span>
          )}
          {hasPromo && (
            <span className="bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              -{discount}%
            </span>
          )}
        </div>
        {inCart && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
            {inCart.QTD}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-800 text-sm leading-tight line-clamp-2">{product.NOME}</h3>
        <div className="mt-1.5 flex items-baseline gap-1.5">
          <span className={`font-bold text-sm ${hasPromo ? 'text-rose-600' : 'text-gray-900'}`}>{fmt(currentPrice)}</span>
          {hasPromo && <span className="text-[11px] text-gray-400 line-through">{fmt(price)}</span>}
        </div>
        <div className="mt-0.5 mb-3">
          {isDigital
            ? <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-0.5"><FiZap className="text-[10px]" />{t('store.instantDelivery')}</span>
            : isOutOfStock
              ? <span className="text-[11px] text-gray-400">{t('store.outOfStock')}</span>
              : <span className="text-[11px] text-gray-400">{t('store.inStock')}</span>
          }
        </div>
        <button
          onClick={handleAdd}
          disabled={isOutOfStock}
          className={`mt-auto w-full py-2 rounded-xl text-xs font-semibold active:scale-95 transition-all flex items-center justify-center gap-1.5
            ${added ? 'bg-emerald-500 text-white' : isOutOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-violet-600 text-white hover:bg-violet-700'}`}
        >
          {added ? <><FiCheck />{t('store.added')}</> : isOutOfStock ? t('store.outOfStock') : <><FiPlus className="text-xs" />{t('common.add')}</>}
        </button>
      </div>
    </div>
  );
};

// ─── Cart Bar ─────────────────────────────────────────────────────────────────

const CartBar: React.FC<{ onCheckout: () => void }> = ({ onCheckout }) => {
  const { t } = useTranslation();
  const { cart, cartTotal, itemCount, updateQuantity } = useCart();
  const [open, setOpen] = useState(false);

  if (itemCount === 0) return null;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 flex items-end" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg mx-auto bg-white rounded-t-3xl p-5 shadow-2xl" onClick={e => e.stopPropagation()} style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))' }}>
            <div className="w-8 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 text-base">{t('store.cart')} <span className="text-gray-400 font-normal">({itemCount})</span></h3>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"><FiX className="text-sm" /></button>
            </div>
            <div className="space-y-3 max-h-52 overflow-y-auto">
              {cart.map(item => (
                <div key={item.ID} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                    {item.IMAGEM && item.MIME_TYPE
                      ? <img src={`data:${item.MIME_TYPE};base64,${item.IMAGEM}`} alt={item.NOME} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><FiPackage className="text-gray-300" /></div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.NOME}</p>
                    <p className="text-xs text-violet-600 font-semibold">{fmt(item.PRECO_PROMOCIONAL || item.PRECO)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 bg-gray-50 rounded-full p-1">
                    <button onClick={() => updateQuantity(item.ID, item.QTD - 1)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-gray-600 shadow-sm hover:bg-gray-50">
                      <FiMinus className="text-xs" />
                    </button>
                    <span className="w-5 text-center text-sm font-bold text-gray-800">{item.QTD}</span>
                    <button onClick={() => updateQuantity(item.ID, item.QTD + 1)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-gray-600 shadow-sm hover:bg-gray-50">
                      <FiPlus className="text-xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">{t('store.totalToPay')}</span>
              <span className="font-bold text-gray-900 text-base">{fmt(cartTotal)}</span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full py-3.5 rounded-2xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
              style={{ boxShadow: '0 4px 15px rgba(124,58,237,0.35)' }}
            >
              <FiLogIn />
              {t('store.signInToPay')}
            </button>
            <p className="text-center text-[11px] text-gray-400 mt-2">{t('store.needAccountToPay')}</p>
          </div>
        </div>
      )}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 w-full max-w-sm px-4">
        <button
          onClick={() => setOpen(true)}
          className="w-full bg-violet-600 text-white rounded-2xl px-5 py-3.5 flex items-center gap-3 transition-all active:scale-[0.98] hover:bg-violet-700"
          style={{ boxShadow: '0 8px 30px rgba(124,58,237,0.4)' }}
        >
          <div className="relative">
            <FiShoppingCart className="text-lg" />
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-violet-700 text-[9px] font-bold flex items-center justify-center">{itemCount}</span>
          </div>
          <span className="flex-1 text-left font-semibold text-sm">{t('store.viewCart')}</span>
          <span className="font-bold text-sm">{fmt(cartTotal)}</span>
          <FiChevronRight />
        </button>
      </div>
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const StorePublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  const [store, setStore] = useState<StoreInfo | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    (async () => {
      try {
        const slugRes = await api.get(`/public/shop/s/${slug}`);
        if (!slugRes.data?.success || !slugRes.data?.data?.storeId) {
          if (slugRes.data?.code === 'STORE_DRAFT') { setIsDraft(true); setLoading(false); return; }
          setNotFound(true); setLoading(false); return;
        }
        if (slugRes.data?.data?.draft) { setIsDraft(true); setLoading(false); return; }
        const id = slugRes.data.data.storeId;
        setStoreId(id);
        const [storeData, productsData, catsData] = await Promise.all([
          ecommerceService.getStoreDetails(id),
          ecommerceService.getStoreProducts(id),
          ecommerceService.getStoreCategories(id),
        ]);
        setStore(storeData as any);
        setProducts(productsData as any);
        setCategories(catsData as any);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleAddToCart = (product: Product) => {
    const cartProduct: CartProduct = {
      ID: product.ID,
      NOME: product.NOME,
      DESCRICAO: product.DESCRICAO,
      PRECO: product.PRECO,
      PRECO_PROMOCIONAL: product.PRECO_PROMOCIONAL,
      IMAGEM: product.IMAGEM,
      MIME_TYPE: product.MIME_TYPE,
      STOCK: product.STOCK,
      LOJA_ID: storeId || undefined,
      TIPO: (product.TIPO || (product as any).tipo || 'FISICO') as any,
    };
    addToCart(cartProduct, 1);
  };

  const handleCheckout = () => {
    navigate(isAuthenticated ? '/ecommerce/checkout' : '/login?redirect=/ecommerce/checkout');
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: store?.DESIGNACAO || 'Loja', url }).catch(() => {});
    } else {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Endpoint returns LOGO (not IMAGEM) for store details
  const logoData = (store as any)?.LOGO || (store as any)?.IMAGEM;
  const logoMime = (store as any)?.MIME_TYPE || 'image/png';
  const logoSrc = logoData ? `data:${logoMime};base64,${logoData}` : null;

  const filtered = products.filter((p) => {
    const cat = (p as any).CATEGORIA || (p as any).categoria || '';
    const matchCat = !activeCategory || cat === activeCategory;
    const matchSearch = !search ||
      p.NOME.toLowerCase().includes(search.toLowerCase()) ||
      (p.DESCRICAO || '').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // ── Draft ────────────────────────────────────────────────────────────────────
  if (isDraft) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
          <FiPackage className="text-3xl text-amber-500" />
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">{t('store.inSetup')}</h1>
        <p className="text-sm text-gray-500 max-w-xs">{t('store.notPublic')}</p>
        <div className="mt-6 flex items-center gap-1.5 text-xs text-gray-400">
          <span className="font-bold text-violet-600">Pagali</span>
        </div>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-100 px-4 h-14 flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3"><Sk className="w-8 h-8 rounded-xl" /><Sk className="h-4 w-28" /></div>
          <Sk className="h-8 w-20 rounded-full" />
        </div>
        <Sk className="h-44 rounded-none" />
        <div className="max-w-2xl mx-auto px-4 py-5">
          <Sk className="h-10 w-full mb-4 rounded-xl" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────────
  if (notFound || !store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-6 text-center bg-gray-50">
        <div className="w-20 h-20 rounded-3xl bg-gray-100 flex items-center justify-center">
          <FiShoppingBag className="text-4xl text-gray-300" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t('store.notFound')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('store.notFoundDesc')}</p>
        </div>
      </div>
    );
  }

  const storeName = (store as any).DESIGNACAO || (store as any).nome || '';
  const hasDigital = products.some(p => p.TIPO === 'DIGITAL' || (p as any).tipo === 'DIGITAL');

  // ── Page ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-24">

      {/* ══ 1. HEADER ══════════════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl overflow-hidden flex-shrink-0 bg-violet-100 flex items-center justify-center ring-1 ring-violet-200">
              {logoSrc
                ? <img src={logoSrc} alt={storeName} className="w-full h-full object-cover" />
                : <span className="text-[10px] font-black text-violet-600">{initials(storeName)}</span>
              }
            </div>
            <span className="font-semibold text-gray-800 text-sm truncate">{storeName}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <select
              value={i18n.language?.slice(0, 2)}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-1.5 py-1 bg-white text-gray-600 cursor-pointer focus:outline-none focus:border-violet-400"
            >
              <option value="pt">PT</option>
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </select>
            <button onClick={handleShare} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
              {copied ? <FiCheck className="text-emerald-500" /> : <FiShare2 className="text-sm" />}
            </button>
            <button onClick={() => navigate(`/login?redirect=/loja/${slug}`)}
              className="px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50 transition-colors">
              {t('common.login')}
            </button>
            <button onClick={() => navigate('/register')}
              className="px-3 py-1.5 rounded-full bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors shadow-sm">
              {t('common.register')}
            </button>
          </div>
        </div>
      </header>

      {/* ══ 2. HERO — logo da loja em destaque ════════════════════════════════ */}
      <div className="relative bg-gradient-to-br from-violet-700 via-violet-600 to-purple-700 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="relative max-w-2xl mx-auto px-4 py-10 flex flex-col items-center text-center">
          {/* Store logo — prominent */}
          <div className="relative mb-5">
            <div className="w-24 h-24 rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white/30 bg-white/10">
              {logoSrc
                ? <img src={logoSrc} alt={storeName} className="w-full h-full object-cover" />
                : (
                  <div className="w-full h-full flex items-center justify-center bg-white/20">
                    <span className="text-3xl font-black text-white">{initials(storeName)}</span>
                  </div>
                )
              }
            </div>
            {/* Verified badge */}
            <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-emerald-400 border-2 border-white flex items-center justify-center shadow-sm">
              <FiCheck className="text-white text-xs font-bold" />
            </div>
          </div>

          <h1 className="text-2xl font-black text-white tracking-tight">{storeName}</h1>
          {(store as any).DESCRICAO && (
            <p className="text-violet-200 text-sm mt-2 max-w-xs leading-relaxed">{(store as any).DESCRICAO}</p>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-3 mt-4">
            <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5">
              <FiShoppingBag className="text-white/80 text-xs" />
              <span className="text-white text-xs font-medium">{products.length} {t('store.products')}</span>
            </div>
            {hasDigital && (
              <div className="flex items-center gap-1.5 bg-emerald-400/30 backdrop-blur-sm rounded-full px-3 py-1.5">
                <FiZap className="text-emerald-200 text-xs" />
                <span className="text-emerald-100 text-xs font-medium">Digital disponível</span>
              </div>
            )}
          </div>
        </div>

        {/* Pagali branding — subtle bottom tag */}
        <div className="absolute bottom-2 right-3 flex items-center gap-1">
          <span className="text-[10px] text-white/40">loja em</span>
          <span className="text-[10px] font-bold text-white/60">Pagali</span>
        </div>
      </div>

      {/* ══ 3. TRUST BAR (ad area 1) ════════════════════════════════════════ */}
      <TrustBar />

      {/* ══ 4. SEARCH + CATEGORIES ══════════════════════════════════════════ */}
      <div className="sticky top-14 z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 space-y-2.5">
          <div className="relative">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder={t('store.searchProducts')} value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors placeholder:text-gray-400" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FiX className="text-sm" />
              </button>
            )}
          </div>
          {categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
              {[{ ID: '', NOME: '' }, ...categories].map((cat) => (
                <button key={cat.ID} onClick={() => setActiveCategory(cat.NOME)}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeCategory === cat.NOME ? 'bg-violet-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-violet-300'
                  }`}>
                  {cat.NOME || t('common.all')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══ 5. PRODUCTS + AD AREAS ══════════════════════════════════════════ */}
      <div className="flex-1 max-w-2xl w-full mx-auto px-4 py-5 space-y-5">
        {filtered.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <FiPackage className="text-2xl text-gray-300" />
            </div>
            <p className="font-medium text-gray-600 text-sm">{t('store.noProducts')}</p>
            {(search || activeCategory) && (
              <button onClick={() => { setSearch(''); setActiveCategory(''); }} className="text-xs text-violet-600 hover:underline">
                {t('store.clearFilters')}
              </button>
            )}
          </div>
        ) : (
          <>
            {search && <p className="text-xs text-gray-400">{filtered.length} {t('store.products')} "{search}"</p>}

            {/* Product grid — with mid-page ad after row 2 (6 products) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filtered.slice(0, 6).map((p) => (
                <ProductCard key={p.ID} product={p} onAdd={handleAddToCart} onDetail={setSelectedProduct} />
              ))}
            </div>

            {/* ── Ad area 2: mid-page Pagali pitch ── */}
            {filtered.length > 3 && (
              <PagaliMidAd onCta={() => navigate('/business/register')} />
            )}

            {/* Remaining products */}
            {filtered.length > 6 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filtered.slice(6).map((p) => (
                  <ProductCard key={p.ID} product={p} onAdd={handleAddToCart} onDetail={setSelectedProduct} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Ad area 3: Pagali services showcase ── */}
        <PagaliServices onRegister={() => navigate('/register')} />
      </div>

      {/* ══ 6. FOOTER ══════════════════════════════════════════════════════ */}
      <footer className="bg-white border-t border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-violet-600 flex items-center justify-center">
              <span className="text-white font-black text-[10px]">P</span>
            </div>
            <span className="text-sm font-bold text-gray-700">Pagali</span>
            <span className="text-xs text-gray-400">· Plataforma de pagamentos digital em Cabo Verde</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/register')} className="text-xs text-violet-600 font-medium hover:underline">{t('common.register')}</button>
            <span className="text-gray-200">|</span>
            <button onClick={() => navigate(`/login?redirect=/loja/${slug}`)} className="text-xs text-gray-500 hover:text-gray-700">{t('common.login')}</button>
          </div>
        </div>
      </footer>

      {/* ══ Floating cart bar ══════════════════════════════════════════════ */}
      <CartBar onCheckout={handleCheckout} />

      {/* ══ Product detail sheet ═══════════════════════════════════════════ */}
      {selectedProduct && (
        <ProductSheet
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAdd={handleAddToCart}
        />
      )}
    </div>
  );
};

export default StorePublic;
